import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  GanttChartSquare, Loader2, Radio, GraduationCap, Clock, Award, Unlock,
  CalendarDays, MapPin, CheckCircle2, ArrowRight, Sparkles, Users, Plus, Trash2, Search, X, ChevronDown, History, Eye, Upload,
} from "lucide-react";
import { PastTrainingDialog, PastTrainingList, ImportHistoryDialog } from "@/components/PastTraining";
import { fmtDate, fmtTime } from "@/lib/helpers";
import { TeamOverview } from "@/components/TeamOverview";

const PORTAL_CATS = ["All", "Live CPD Training", "E-learning", "Safeguarding", "SEND",
  "First Aid", "HR, Compliance and Consultancy", "Training Programmes"];

export default function MemberPortal() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
  const [password, setPassword] = useState("");
  const forgot = async () => {
    if (!email.trim()) return toast.error("Enter your membership email first");
    try {
      const { data } = await api.post("/membership/forgot-password", { email: email.trim() });
      toast.success(data.message);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState("gate"); // gate | active | inactive
  const [courses, setCourses] = useState([]);
  const [courseQ, setCourseQ] = useState("");
  const [cat, setCat] = useState("All");
  const catsOf = (c) => (c.categories?.length ? c.categories : [c.category]);
  const shownCourses = courses.filter((c) => (cat === "All" || catsOf(c).includes(cat)) && c.title.toLowerCase().includes(courseQ.trim().toLowerCase()));
  const [liveCourse, setLiveCourse] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    api.get("/membership/me").then(async ({ data }) => {
      setEmail(data.email); setName(data.name || ""); setViewer(data.impersonated_by || null);
      const res = await api.get("/membership/courses");
      setCourses(res.data);
      setState("active");
    }).catch(() => {});
  }, []);
  const exitView = async () => {
    await api.post("/membership/logout").catch(() => {});
    window.close();
    setState("gate"); setViewer(null);
  };

  const findCourses = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Enter your membership email and password");
    setLoading(true);
    try {
      const { data } = await api.post("/membership/login", { email, password });
      if (data.name) setName(data.name);
      if (!data.active) { setState("inactive"); return; }
      const res = await api.get("/membership/courses");
      setCourses(res.data);
      setState("active");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid email or password");
    }
    finally { setLoading(false); }
  };

  const doUnlock = async (courseId, sessionId) => {
    try {
      const { data } = await api.post("/payments/member-unlock", {
        course_id: courseId, candidate_name: name || email.split("@")[0],
        candidate_email: email, origin_url: window.location.origin, session_id: sessionId || null,
      });
      toast.success("Unlocked! Check your email for the access link.");
      navigate(`/access/${data.access_token}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unlock = (course) => {
    if (course.course_type === "live") setLiveCourse(course);
    else doUnlock(course.id, null);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border backdrop-blur-md bg-white/90 dark:bg-slate-900/90">
        <div className="max-w-5xl mx-auto h-full flex items-center px-4 sm:px-6 gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center overflow-hidden">{branding.logo_url ? <img src={branding.logo_url} alt="" className="h-full w-full object-contain" /> : <GanttChartSquare className="h-5 w-5 text-white" />}</div>
          <span className="font-heading font-bold text-lg">{branding.platform_name}</span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => navigate("/shop")} data-testid="portal-shop-button">Course shop</Button>
        </div>
      </header>

      {viewer && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-sm" data-testid="view-as-banner">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
            <Eye className="h-4 w-4" /><span><b>Admin view:</b> you're signed in as <b>{name || email}</b>. Anything you add or book here is done for this member.</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" className="h-7 bg-white" onClick={exitView} data-testid="view-as-exit-button">Exit member view</Button>
          </div>
        </div>
      )}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {state !== "active" && (
          <div className="max-w-md mx-auto text-center stagger-in">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5"><Sparkles className="h-7 w-7 text-primary" /></div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Member Portal</h1>
            <p className="text-sm text-muted-foreground mt-2">Sign in with your membership email and password to unlock every course included free with your plan.</p>
            <Card className="p-6 mt-8 text-left">
              <form onSubmit={findCourses} className="space-y-4">
                <div><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Member" className="mt-1.5" data-testid="portal-name-input" /></div>
                <div><Label>Membership email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5" data-testid="portal-email-input" /></div>
                <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="mt-1.5" data-testid="portal-password-input" /></div>
                {state === "inactive" && <p className="text-sm text-red-600" data-testid="portal-inactive-msg">No active membership found for this email. <button type="button" className="underline" onClick={() => navigate("/shop?cat=memberships")}>Browse membership plans</button></p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="portal-find-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
                <button type="button" onClick={forgot} className="text-sm text-primary hover:underline" data-testid="portal-forgot-password">First time or forgot your password? Set it here</button>
              </form>
            </Card>
          </div>
        )}

        {state === "active" && (
          <div className="stagger-in">
            <div className="flex items-center gap-2 text-sm text-emerald-600 mb-2"><CheckCircle2 className="h-4 w-4" />Active membership · {email}</div>
            <TeamSection managerName={name} />
            <h1 className="font-heading text-3xl font-bold tracking-tight">Your free courses</h1>
            <p className="text-sm text-muted-foreground mt-1">{courses.length} courses included with your plan. Unlock any instantly.</p>
            <div className="relative max-w-md mt-5">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={courseQ} onChange={(e) => setCourseQ(e.target.value)} placeholder="Search your courses…" className="pl-9" data-testid="portal-course-search-input" />
            </div>
            <div className="flex gap-2 flex-wrap mt-4" data-testid="portal-category-filters">
              {PORTAL_CATS.map((c) => (
                <button key={c} type="button" onClick={() => setCat(c)} data-testid={`portal-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${cat === c ? "bg-primary text-white border-primary" : "border-border hover:bg-accent/40"}`}>
                  {c}
                </button>
              ))}
            </div>
            {shownCourses.length === 0 && (
              <p className="text-sm text-muted-foreground mt-6" data-testid="portal-course-search-empty">No matching courses</p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {shownCourses.map((c) => (
                <Card key={c.id} className="overflow-hidden border-border flex flex-col" data-testid={`portal-course-${c.id}`}>
                  <div className="relative h-36 bg-slate-200 dark:bg-slate-800 overflow-hidden">
                    {c.thumbnail && <img src={c.thumbnail} alt={c.title} className="h-full w-full object-cover" />}
                    <Badge className={`absolute top-3 left-3 border-0 text-white ${c.course_type === "live" ? "bg-red-500 hover:bg-red-500" : "bg-emerald-600 hover:bg-emerald-600"}`}>
                      {c.course_type === "live" ? <><Radio className="h-3 w-3 mr-1" />LIVE</> : <><GraduationCap className="h-3 w-3 mr-1" />CPD</>}
                    </Badge>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.category}</span>
                    <h3 className="font-heading font-semibold text-base leading-snug mt-1 line-clamp-2 min-h-[2.75rem]">{c.title}</h3>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.duration_hours}h</span>
                      {c.cpd_points > 0 && <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" />{c.cpd_points} CPD pts</span>}
                    </div>
                    <Button size="sm" className="mt-4 w-full" onClick={() => unlock(c)} data-testid={`portal-unlock-${c.id}-button`}><Unlock className="h-4 w-4 mr-1.5" />Unlock free</Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {liveCourse && <LiveSessionDialog course={liveCourse} onClose={() => setLiveCourse(null)} onPick={(sid) => { doUnlock(liveCourse.id, sid); setLiveCourse(null); }} />}
    </div>
  );
}

function TeamSection({ managerName }) {
  const [info, setInfo] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cname, setCname] = useState("");
  const [cemail, setCemail] = useState("");
  const [adding, setAdding] = useState(false);
  const [enrolFor, setEnrolFor] = useState(null);
  const [pending, setPending] = useState({});
  const [booking, setBooking] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [openLearners, setOpenLearners] = useState([]);
  const [listOpen, setListOpen] = useState(false);
  const [lq, setLq] = useState("");
  const [pastFor, setPastFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const toggleLearner = (id) => setOpenLearners(openLearners.includes(id) ? openLearners.filter((x) => x !== id) : [...openLearners, id]);
  const shown = candidates.filter((c) => !lq || `${c.name} ${c.email}`.toLowerCase().includes(lq.toLowerCase()));
  const bookTeam = async (ids, course, sid) => {
    setBooking("team");
    try {
      const { data } = await api.post("/membership/enrol-team", { candidate_ids: ids, course_id: course.id, session_id: sid || null });
      toast.success(`Booked ${data.booked} learner(s) onto ${course.title}` + (data.emailed < data.booked ? ` (${data.booked - data.emailed} email(s) could not be sent)` : " - details emailed"));
      setTeamOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBooking(null);
  };

  const load = async () => {
    try {
      const [i, c, cr] = await Promise.all([
        api.get("/membership/me"), api.get("/membership/candidates"), api.get("/membership/courses"),
      ]);
      setInfo(i.data); setCandidates(c.data); setCourses(cr.data);
    } catch (e) { /* not a team membership session */ }
  };
  useEffect(() => { load(); }, []);

  const addCandidate = async (e) => {
    e?.preventDefault();
    if (!cname.trim() || !cemail.trim()) return toast.error("Enter the candidate's name and email");
    setAdding(true);
    try {
      await api.post("/membership/candidates", { name: cname.trim(), email: cemail.trim() });
      toast.success("Candidate added");
      setCname(""); setCemail(""); load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setAdding(false); }
  };
  const removeCandidate = async (id) => {
    if (!window.confirm("Remove this candidate and their enrolments?")) return;
    try { await api.delete(`/membership/candidates/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addPending = (candidateId, course, session) => {
    const list = pending[candidateId] || [];
    if (list.some((p) => p.course_id === course.id) || candidates.find((c) => c.id === candidateId)?.enrolments?.some((e) => e.course_id === course.id)) {
      return toast.error("That course is already on this learner's list");
    }
    setPending({ ...pending, [candidateId]: [...list, { course_id: course.id, title: course.title, session: session || null }] });
    setEnrolFor(null);
    toast.success("Course added - click Confirm & book when you're ready");
  };
  const removePending = (candidateId, courseId) => setPending({ ...pending, [candidateId]: (pending[candidateId] || []).filter((p) => p.course_id !== courseId) });
  const confirmBook = async (candidateId) => {
    const items = pending[candidateId] || [];
    if (!items.length) return;
    setBooking(candidateId);
    try {
      const { data } = await api.post("/membership/enrol-batch", { candidate_id: candidateId, items: items.map((p) => ({ course_id: p.course_id, session_id: p.session?.id || null })) });
      toast.success(data.emailed ? `Booked ${data.enrolments.length} course(s) - details emailed to the learner` : "Booked (email could not be sent)");
      setPending({ ...pending, [candidateId]: [] });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBooking(null);
  };

  const full = info && info.used >= info.seats;

  return (
    <Card className="p-6 mb-8" data-testid="team-section">
      {teamOpen && <EnrolDialog team={candidates} courses={courses} onClose={() => setTeamOpen(false)} onTeam={bookTeam} busy={booking === "team"} />}
      {enrolFor && <EnrolDialog candidate={enrolFor} courses={courses} onClose={() => setEnrolFor(null)} onEnrol={addPending} />}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Your team</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Add candidates and enrol each onto the courses included with your membership.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="import-history-button"><Upload className="h-4 w-4 mr-1.5" />Import previous training</Button>
          {candidates.length > 1 && <Button size="sm" onClick={() => setTeamOpen(true)} data-testid="book-team-button"><Users className="h-4 w-4 mr-1.5" />Book team onto a course</Button>}
          {info && <Badge variant="outline" data-testid="seats-badge">{info.used} / {info.seats} seats used</Badge>}
        </div>
      </div>

      <form onSubmit={addCandidate} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mt-5">
        <Input value={cname} onChange={(e) => setCname(e.target.value)} placeholder="Candidate name" data-testid="candidate-name-input" />
        <Input type="email" value={cemail} onChange={(e) => setCemail(e.target.value)} placeholder="candidate@email.com" data-testid="candidate-email-input" />
        <Button type="submit" disabled={adding || full} data-testid="add-candidate-button"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
      </form>
      {full && <p className="text-xs text-amber-600 mt-2">You've used all your seats. Remove a candidate or ask us to add more seats.</p>}

      <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
        <button type="button" onClick={() => setListOpen(!listOpen)} className="font-heading text-lg font-semibold flex items-center gap-2 hover:text-primary" data-testid="team-list-toggle">
          <ChevronDown className={`h-4 w-4 transition-transform ${listOpen ? "" : "-rotate-90"}`} /><Users className="h-5 w-5 text-primary" />Learners ({candidates.length})
        </button>
        {listOpen && candidates.length > 3 && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button type="button" className="text-xs text-primary hover:underline whitespace-nowrap" onClick={() => setOpenLearners(openLearners.length ? [] : candidates.map((c) => c.id))} data-testid="team-expand-all">{openLearners.length ? "Collapse all" : "Expand all"}</button>
            <div className="relative w-full sm:w-56"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" /><Input value={lq} onChange={(e) => setLq(e.target.value)} placeholder="Find a learner" className="pl-8 h-9" data-testid="team-search-input" /></div>
          </div>
        )}
      </div>
      {listOpen && <div className="mt-3 space-y-2">
        {candidates.length === 0 && <p className="text-sm text-muted-foreground">No candidates yet. Add your first above.</p>}
        {candidates.length > 0 && shown.length === 0 && <p className="text-sm text-muted-foreground" data-testid="team-search-empty">No learners match your search.</p>}
        {shown.map((c) => {
          const isOpen = openLearners.includes(c.id) || (pending[c.id] || []).length > 0;
          return (
          <div key={c.id} className="rounded-lg border border-border" data-testid={`candidate-${c.id}`}>
            <div className="flex items-center justify-between gap-3 p-3 cursor-pointer hover:bg-accent/30 rounded-lg" onClick={() => toggleLearner(c.id)} data-testid={`candidate-toggle-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                <div className="min-w-0">
                  <p className="font-medium truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">{c.enrolments?.length || 0} course(s){c.history?.length ? ` · ${c.history.length} previous` : ""}{(pending[c.id] || []).length ? ` · ${pending[c.id].length} to book` : ""}</span>
                <Button variant="ghost" size="icon" onClick={(ev) => { ev.stopPropagation(); removeCandidate(c.id); }} data-testid={`remove-candidate-${c.id}`} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            {isOpen && <div className="px-4 pb-4">
            {c.enrolments?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {c.enrolments.map((e) => <span key={e.id} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400">{e.course_title}{e.session ? ` · ${fmtDate(e.session.start)}` : ""}{e.certificate ? ` · Completed ${fmtDate(e.certificate.issue_date)}` : ""}</span>)}
              </div>
            )}
            {(pending[c.id] || []).length > 0 && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:bg-amber-500/10" data-testid={`pending-${c.id}`}>
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Ready to book (not booked yet)</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {pending[c.id].map((p) => (
                    <span key={p.course_id} className="text-[11px] pl-2 pr-1 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900 flex items-center gap-1" data-testid={`pending-${c.id}-${p.course_id}`}>
                      {p.title}{p.session ? ` · ${fmtDate(p.session.start)} ${fmtTime(p.session.start)}` : ""}
                      <button onClick={() => removePending(c.id, p.course_id)} data-testid={`pending-remove-${c.id}-${p.course_id}`}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setEnrolFor(c)} data-testid={`enrol-button-${c.id}`}><Search className="h-4 w-4 mr-1.5" />Find & add a course</Button>
              <Button size="sm" variant="outline" onClick={() => setPastFor(c)} data-testid={`past-training-button-${c.id}`}><History className="h-4 w-4 mr-1.5" />Add previous training</Button>
              {(pending[c.id] || []).length > 0 && (
                <Button size="sm" onClick={() => confirmBook(c.id)} disabled={booking === c.id} data-testid={`confirm-book-${c.id}`}>
                  {booking === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" />Confirm & book ({pending[c.id].length})</>}
                </Button>
              )}
            </div>
            <PastTrainingList items={c.history} onChanged={load} />
            </div>}
          </div>
          );
        })}
      </div>}
      {candidates.length > 0 && <TeamOverview candidates={candidates} />}
      {pastFor && <PastTrainingDialog learner={pastFor} courses={courses} onClose={() => setPastFor(null)} onSaved={() => { setPastFor(null); load(); }} />}
      {importOpen && <ImportHistoryDialog onClose={() => setImportOpen(false)} onDone={load} />}
    </Card>
  );
}

function EnrolDialog({ candidate, courses, onClose, onEnrol, team, onTeam, busy }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [sid, setSid] = useState("");
  const eligible = team ? team.filter((m) => !course || !m.enrolments?.some((e) => e.course_id === course.id)) : [];
  const pick = (c) => {
    setCourse(c); setSid(""); setSessions(null);
    if (c.course_type === "live") api.get(`/shop/courses/${c.id}/sessions`).then(({ data }) => setSessions(data)).catch(() => setSessions([]));
  };
  const list = courses.filter((c) => c.title.toLowerCase().includes(q.trim().toLowerCase()));
  const isLive = course?.course_type === "live";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enrol-dialog">
        <DialogHeader><DialogTitle className="font-heading">{team ? "Book your team onto a course" : `Add a course for ${candidate.name}`}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses…" className="pl-9" data-testid="enrol-search-input" />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {list.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No matching courses</p>}
          {list.map((c) => (
            <button key={c.id} onClick={() => pick(c)} data-testid={`enrol-course-${c.id}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm border transition-colors ${course?.id === c.id ? "border-primary bg-accent/50" : "border-transparent hover:bg-accent/30"}`}>
              <span className="font-medium">{c.title}</span>
              <span className="ml-2 text-xs text-muted-foreground">{c.course_type === "live" ? "Live" : "E-learning / CPD"}</span>
            </button>
          ))}
        </div>
        {isLive && (
          <div>
            <Label>Choose a session date</Label>
            {sessions === null ? <Loader2 className="h-5 w-5 animate-spin text-primary mt-2" /> : sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-1.5">No upcoming dates scheduled yet.</p>
            ) : (
              <div className="space-y-1.5 mt-1.5">
                {sessions.map((s) => (
                  <button key={s.id} onClick={() => setSid(s.id)} disabled={s.seats_left === 0} data-testid={`enrol-session-${s.id}`}
                    className={`w-full text-left p-2.5 rounded-lg border text-sm disabled:opacity-50 ${sid === s.id ? "border-primary bg-accent/50" : "border-border hover:bg-accent/30"}`}>
                    <span className="font-medium">{fmtDate(s.start)} · {fmtTime(s.start)}</span>
                    <span className="text-xs text-muted-foreground ml-2">{s.location}{s.seats_left !== undefined ? ` · ${s.seats_left} seats left` : ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {team && course && (
          <div data-testid="team-learners">
            <div className="flex items-center justify-between">
              <Label>Choose learners</Label>
              <button type="button" className="text-xs text-primary hover:underline" onClick={() => setPicked(picked.length === eligible.length ? [] : eligible.map((m) => m.id))} data-testid="team-select-all">{picked.length === eligible.length && eligible.length ? "Clear all" : "Select all"}</button>
            </div>
            {eligible.length === 0 ? <p className="text-sm text-muted-foreground mt-1.5">Everyone is already booked on this course.</p> : (
              <div className="space-y-1 mt-1.5 max-h-48 overflow-y-auto">
                {eligible.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 p-2 rounded-md border border-border text-sm cursor-pointer hover:bg-accent/30" data-testid={`team-learner-${m.id}`}>
                    <input type="checkbox" checked={picked.includes(m.id)} onChange={() => toggle(m.id)} className="h-4 w-4 accent-primary" />
                    <span className="font-medium">{m.name}</span><span className="text-xs text-muted-foreground">{m.email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {team ? (
            <Button disabled={busy || !course || (isLive && !sid) || !picked.filter((id) => eligible.some((m) => m.id === id)).length}
              onClick={() => onTeam(picked.filter((id) => eligible.some((m) => m.id === id)), course, sid)} data-testid="team-book-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm & book ${picked.filter((id) => eligible.some((m) => m.id === id)).length} learner(s)`}
            </Button>
          ) : (
          <Button disabled={!course || (isLive && !sid)} onClick={() => onEnrol(candidate.id, course, sessions?.find((s) => s.id === sid))} data-testid="enrol-confirm-button">Add to list</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LiveSessionDialog({ course, onClose, onPick }) {
  const [sessions, setSessions] = useState(null);
  const [sid, setSid] = useState("");

  if (sessions === null) {
    api.get(`/shop/courses/${course.id}/sessions`).then(({ data }) => setSessions(data)).catch(() => setSessions([]));
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Choose a date — {course.title}</DialogTitle></DialogHeader>
        {sessions === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No upcoming dates scheduled yet. Please check back soon.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <button key={s.id} onClick={() => setSid(s.id)} data-testid={`portal-session-${s.id}`}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${sid === s.id ? "border-primary bg-accent/50" : "border-border hover:bg-accent/30"}`}>
                <span className="text-sm font-medium flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-primary" />{fmtDate(s.start)}</span>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime(s.start)}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.location}</span>
                </div>
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onPick(sid)} disabled={!sid} data-testid="portal-confirm-unlock-button">Unlock free <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
